import { useEffect, useState } from "react";

/**
 * Turns a raw "loading" flag into one that only flips true after a short
 * delay — so a fetch that resolves in a blink (data already cached, or a
 * fast connection) never gets a loading skeleton flashed in front of the
 * real content for a single frame. The moment `active` goes false, this
 * drops back to false immediately — no delay on the way out, only on the
 * way in.
 *
 * Usage: keep the real `loading` state exactly as it is for everything else
 * (disabling buttons, gating fetches, etc.) — only swap in this value for
 * the "should I render a LoadingPanel/skeleton right now" decision.
 *
 *   const showLoading = useDelayedLoading(loading);
 *   return showLoading ? <LoadingPanel /> : ...
 */
export function useDelayedLoading(active, delay = 200) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!active) {
      setShow(false);
      return undefined;
    }
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [active, delay]);

  return show;
}
